import { createContext, FC, ReactNode, RefObject, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * Cross-cutting state for the "Set a class focus" onboarding task. It lets the onboarding
 * card (which lives in one branch of the dashboard tree) drive the attention effects on the
 * Unit Progress widget (which lives in another) without threading setters through every level
 * in between. See {@link useClassFocusOnboarding}.
 */
interface ClassFocusOnboardingContextValue {
  /** True once any student in scope has submitted their diagnostic — unblocks the task. */
  anyStudentCompletedDiagnostic: boolean;
  setAnyStudentCompletedDiagnostic: (value: boolean) => void;
  /** True once the teacher activates a focus — auto-completes the onboarding task. */
  classFocusCompleted: boolean;
  markClassFocusCompleted: () => void;
  /** Whether the Set-focus button and unit dropdown should pulse to draw attention. */
  pulsing: boolean;
  /** Whether the coachmark should render — pulsing AND never dismissed by this teacher. */
  coachmarkVisible: boolean;
  /** Attach to the Unit Progress widget so the task click can scroll it into view. */
  focusAnchorRef: RefObject<HTMLDivElement>;
  /** Triggered from the onboarding task: smooth-scroll to the widget and start pulsing. */
  requestFocusAttention: () => void;
  /** Dismiss the coachmark for good (persisted); the scroll and pulse keep working afterwards. */
  dismissCoachmark: () => void;
  /** Stop the pulse (the focus flow was completed or abandoned). */
  stopPulsing: () => void;
}

// Once dismissed, the coachmark never comes back — the smooth scroll and pulse are enough
// guidance from then on. Persisted per browser so a reload doesn't resurrect it.
export const COACHMARK_DISMISSED_KEY = 'class-focus-coachmark-dismissed';

const ClassFocusOnboardingContext = createContext<ClassFocusOnboardingContextValue | null>(null);

export const ClassFocusOnboardingProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const focusAnchorRef = useRef<HTMLDivElement>(null);
  const [anyStudentCompletedDiagnostic, setAnyStudentCompletedDiagnostic] = useState(false);
  const [classFocusCompleted, setClassFocusCompleted] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [coachmarkDismissed, setCoachmarkDismissed] = useState(
    () => localStorage.getItem(COACHMARK_DISMISSED_KEY) === '1',
  );

  const requestFocusAttention = useCallback(() => {
    const anchor = focusAnchorRef.current;
    if (anchor) {
      // The footer is fixed over the viewport bottom, so scroll the widget to sit a comfortable
      // 40px above the footer's top edge (footer height + breathing room) instead of behind it.
      // The anchor is the widget itself, so this works whether or not a class is in scope —
      // without one the focus controls render inert and there's nothing to pulse.
      const footerHeight = document.querySelector<HTMLElement>('.nc_footer')?.offsetHeight ?? 0;
      const margin = footerHeight + 100;
      const anchorBottom = window.scrollY + anchor.getBoundingClientRect().bottom;
      window.scrollTo({ top: anchorBottom - (window.innerHeight - margin), behavior: 'smooth' });
    }
    setPulsing(true);
  }, []);

  const dismissCoachmark = useCallback(() => {
    localStorage.setItem(COACHMARK_DISMISSED_KEY, '1');
    setCoachmarkDismissed(true);
  }, []);

  const stopPulsing = useCallback(() => setPulsing(false), []);

  const markClassFocusCompleted = useCallback(() => {
    setClassFocusCompleted(true);
    setPulsing(false);
  }, []);

  const value = useMemo<ClassFocusOnboardingContextValue>(
    () => ({
      anyStudentCompletedDiagnostic,
      setAnyStudentCompletedDiagnostic,
      classFocusCompleted,
      markClassFocusCompleted,
      pulsing,
      coachmarkVisible: pulsing && !coachmarkDismissed,
      focusAnchorRef,
      requestFocusAttention,
      dismissCoachmark,
      stopPulsing,
    }),
    [
      anyStudentCompletedDiagnostic,
      classFocusCompleted,
      pulsing,
      coachmarkDismissed,
      markClassFocusCompleted,
      requestFocusAttention,
      dismissCoachmark,
      stopPulsing,
    ],
  );

  return <ClassFocusOnboardingContext.Provider value={value}>{children}</ClassFocusOnboardingContext.Provider>;
};

export const useClassFocusOnboarding = (): ClassFocusOnboardingContextValue => {
  const context = useContext(ClassFocusOnboardingContext);
  if (!context) {
    throw new Error('useClassFocusOnboarding must be used within a ClassFocusOnboardingProvider');
  }
  return context;
};
